// cloudinaryRef.ts

import { Cloudinary } from 'cloudinary-core'

import AppConfig from '../config/AppConfig'

let cl: Cloudinary | undefined

function requireInit(): Cloudinary {
  if (!cl) {
    throw new Error('Cloudinary not initialized. Call initCloudinary() first.')
  }
  return cl
}

export const getImageMetadata = (
  publicId: string,
  options: Record<string, unknown> = {},
): string => {
  const c = requireInit()
  return c.url(publicId, {
    flags: 'getinfo',
    ...options,
  } as any) // cloudinary-core flags/getinfo isn't in all typings; cast to keep flexibility
}

export const convertS3Logo = ({
  logoUrl,
  pattern,
  publicIdPrefix,
}: {
  logoUrl: string | null
  pattern: RegExp
  publicIdPrefix: string
}): string | undefined => {
  if (!logoUrl) return undefined

  const match = logoUrl.match(pattern)
  if (!match || typeof match[1] !== 'string') return logoUrl

  const captured = match[1]
  // sanitize captured part and strip query params or hash
  const tail = captured.split(/[?#]/)[0]

  const publicId = `${publicIdPrefix}/${tail}`
  return tail ? publicId : undefined
}

export const initCloudinary = (): void => {
  cl = new Cloudinary({
    cloud_name: AppConfig.CLOUD_NAME,
    secure: true,
  })
}

const cloudinaryRef = {
  instance: (): Cloudinary => requireInit(),

  getCloudinaryUrl: (
    ...args: Parameters<Cloudinary['url']>
  ): ReturnType<Cloudinary['url']> => {
    const c = requireInit()
    return c.url(...args)
  },

  getCloudinaryVideoUrl: (
    ...args: Parameters<Cloudinary['video_url']>
  ): ReturnType<Cloudinary['video_url']> => {
    const c = requireInit()
    return c.video_url(...args)
  },

  getImageMetadata,

  getVideoThumbnailUrl: (
    ...args: Parameters<Cloudinary['video_thumbnail_url']>
  ): ReturnType<Cloudinary['video_thumbnail_url']> => {
    const c = requireInit()
    return c.video_thumbnail_url(...args)
  },

  getBrandLogoUrl: ({
    logoUrl,
    options = {},
  }: {
    logoUrl?: string | null
    options?: Parameters<Cloudinary['url']>[1]
  }): ReturnType<Cloudinary['url']> | string | undefined => {
    if (!logoUrl) return undefined
    // Default cloudinary options for brand logos, with the ability to override
    const cloudinaryOptions = {
      ...{
        background: 'auto:predominant',
        crop: 'pad',
        width: 200,
        height: 200,
        fetch_format: 'jpg',
        quality: 'auto',
      },
      ...options,
    }
    const publicId = convertS3Logo({
      logoUrl,
      pattern: /brands\/logos\/(.+)/,
      publicIdPrefix: 'brand-logos',
    })

    if (!publicId) {
      return logoUrl // Return original URL if conversion fails
    }

    const c = requireInit()
    const url = c.url(publicId, {
      ...cloudinaryOptions, // standard transform args go here
    })

    return url
  },
}

export default cloudinaryRef
